import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  TagIcon, 
  UserGroupIcon, 
  ArrowRightIcon,
  CheckCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const PartnershipsSection: React.FC = () => {
  const [hoveredPartner, setHoveredPartner] = useState<number | null>(null);

  const partners = [
    {
      id: 1,
      name: 'Google Ads',
      logo: '🎯',
      discount: 'خصم 20%',
      description: 'على أول إنفاق إعلاني',
      benefit: 'تقليل تكلفة الحملات',
      savings: '12,000 ر.س',
      users: '2,847',
      category: 'تسويق رقمي',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 2,
      name: 'Snap Business',
      logo: '👻',
      discount: 'كاش باك 10%',
      description: 'على جميع الحملات الإعلانية',
      benefit: 'تحفيز الإعلانات بفعالية',
      savings: '8,500 ر.س',
      users: '1,923',
      category: 'وسائل التواصل',
      color: 'from-yellow-400 to-yellow-500'
    },
    {
      id: 3,
      name: 'Zoho One',
      logo: '⚡',
      discount: 'خصم 30%',
      description: 'لمدة سنة كاملة',
      benefit: 'دعم التحول الرقمي للأقسام',
      savings: '15,600 ر.س',
      users: '3,456',
      category: 'إدارة الأعمال',
      color: 'from-green-500 to-green-600'
    },
    {
      id: 4,
      name: 'STC Business',
      logo: '📱',
      discount: 'باقة مخفضة',
      description: 'رسوم مخفضة للشركات',
      benefit: 'تسهيلات في الاتصال والتقنية',
      savings: '6,200 ر.س',
      users: '4,123',
      category: 'اتصالات',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 5,
      name: 'Aramex',
      logo: '📦',
      discount: 'خصم 15%',
      description: 'على جميع الشحنات',
      benefit: 'خفض مصاريف التوصيل',
      savings: '4,800 ر.س',
      users: '1,567',
      category: 'شحن ولوجستيات',
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 6,
      name: 'Microsoft 365',
      logo: '💼',
      discount: 'خصم 25%',
      description: 'على الاشتراكات السنوية',
      benefit: 'أدوات إنتاجية متطورة',
      savings: '9,300 ر.س',
      users: '2,891',
      category: 'إنتاجية',
      color: 'from-indigo-500 to-indigo-600'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="partnerships" className="py-20 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full glass-card text-sm font-medium text-primary-600 dark:text-primary-400 mb-6">
            <SparklesIcon className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2" />
            شبكة الشراكات الذكية
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            أبرز الشراكات
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            وفر آلاف الريالات سنوياً من خلال خصومات تلقائية مع شركائنا المعتمدين
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-primary-600 dark:text-primary-400 mb-2">
              50+
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              شريك معتمد
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-primary-600 dark:text-primary-400 mb-2">
              2.3 مليون ر.س
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              إجمالي الوفورات
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-primary-600 dark:text-primary-400 mb-2">
              15K+
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              شركة مستفيدة
            </div>
          </div>
        </motion.div>

        {/* Partners Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {partners.map((partner, index) => (
            <motion.div
              key={partner.id}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              onHoverStart={() => setHoveredPartner(partner.id)}
              onHoverEnd={() => setHoveredPartner(null)}
              className="group relative"
            >
              <div className="glass-card p-6 h-full hover:shadow-2xl transition-all duration-300 border border-gray-200/50 dark:border-gray-700/50">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3 rtl:space-x-reverse">
                    <div className="text-3xl">{partner.logo}</div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white">
                        {partner.name}
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {partner.category}
                      </span>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${partner.color}`}>
                    {partner.discount}
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  {partner.description}
                </p>

                {/* Benefit */}
                <div className="flex items-center space-x-2 rtl:space-x-reverse mb-4">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {partner.benefit}
                  </span>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {partner.savings}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      متوسط الوفورات
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {partner.users}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      شركة مستفيدة
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-3 px-4 rounded-lg font-semibold text-white bg-gradient-to-r ${partner.color} hover:shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 rtl:space-x-reverse`}
                >
                  <span>استفد الآن</span>
                  <ArrowRightIcon className="w-4 h-4 rtl:rotate-180" />
                </motion.button>

                {/* Hover Glow Effect */}
                <div 
                  className={`absolute inset-0 rounded-xl bg-gradient-to-r ${partner.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none`}
                />

                {/* Active Indicator */}
                {hoveredPartner === partner.id && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center"
                  >
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <div className="glass-card p-8 max-w-3xl mx-auto">
            <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse mb-4">
              <TagIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                شراكات حصرية أكثر قريباً
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              نعمل باستمرار على إضافة شركاء جدد لتوفير المزيد من الفرص لتوفير التكاليف
            </p>
            <div className="flex items-center justify-center space-x-4 rtl:space-x-reverse">
              <UserGroupIcon className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                +500 شركة جديدة تنضم شهرياً
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnershipsSection;
